import { defineStore } from 'pinia';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { useIdStore } from '@/src/store/id';

export interface ModelMetadata {
  name: string;
}

export interface ModelDisplayConfig {
  visible: boolean;
  opacity: number;
  /** RGB components in the 0–1 range. */
  color: [number, number, number];
}

const DEFAULT_DISPLAY: ModelDisplayConfig = {
  visible: true,
  opacity: 1,
  color: [0.92, 0.9, 0.86],
};

interface State {
  idList: string[];
  dataIndex: Record<string, vtkPolyData>;
  metadata: Record<string, ModelMetadata>;
  display: Record<string, ModelDisplayConfig>;
}

export const useModelStore = defineStore('models', {
  state: (): State => ({
    idList: [],
    dataIndex: {},
    metadata: {},
    display: {},
  }),
  actions: {
    addVTKPolyData(name: string, polyData: vtkPolyData) {
      const id = useIdStore().nextId();
      this.idList.push(id);
      this.dataIndex[id] = polyData;
      this.metadata[id] = { name };
      this.display[id] = { ...DEFAULT_DISPLAY };
      return id;
    },
    removeModel(id: string) {
      if (!(id in this.dataIndex)) return;
      delete this.dataIndex[id];
      delete this.metadata[id];
      delete this.display[id];
      this.idList = this.idList.filter((modelId) => modelId !== id);
    },
    getModelName(id: string) {
      return this.metadata[id]?.name ?? `Mesh ${id}`;
    },
    getModelDisplay(id: string): ModelDisplayConfig {
      return this.display[id] ?? { ...DEFAULT_DISPLAY };
    },
    updateModelDisplay(id: string, update: Partial<ModelDisplayConfig>) {
      if (!(id in this.dataIndex)) return;
      this.display[id] = {
        ...this.getModelDisplay(id),
        ...update,
      };
    },
  },
});
